import { platforms_SNS } from 'src/utils/platforms';

/** 최소 반응 수 — 빈 값·음수·소수는 0 또는 내림 정수로 좁힌다 */
export function normalizeMetricMinValue(val: unknown): number {
  if (val === '' || val === null || val === undefined) return 0;
  const num = Number(val);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.floor(num);
}

/** 언어(tL)는 소문자 2자리 코드로 저장한다. */
export function normalizeLanguageCodes(list: unknown): string[] {
  return Array.from(new Set(
    (Array.isArray(list) ? list : [])
      .map((v) => String(v || '').trim().toLowerCase())
      .filter((v) => v !== '')
  ));
}

/** 강화 수집 한 줄 — 키워드 하나에 채널 여럿 */
export type EnhancedCollection = { keyword: string; platforms: string[] };

/** 강화 수집 설정은 현재 캠페인에 살아있는 키워드 것만, 채널이 하나라도 켜진 항목만 남긴다. */
export function normalizeEnhancedCollections(list: unknown, keywords: unknown): EnhancedCollection[] {
  const keywordSet = new Set(
    (Array.isArray(keywords) ? keywords : []).map((k) => String(k || '').trim()).filter((k) => k !== '')
  );
  const seen = new Set();
  return (Array.isArray(list) ? list : []).reduce((acc: EnhancedCollection[], entry: any) => {
    const keyword = String(entry?.keyword ?? '').trim();
    if (keyword === '' || !keywordSet.has(keyword) || seen.has(keyword)) return acc;
    const platforms: string[] = Array.from(new Set(
      (Array.isArray(entry?.platforms) ? entry.platforms : [])
        .map((v: unknown) => String(v || '').trim())
        .filter((v: string) => v !== '')
    ));
    if (platforms.length === 0) return acc;
    seen.add(keyword);
    acc.push({ keyword, platforms });
    return acc;
  }, [] as EnhancedCollection[]);
}

/**
 * 강화 수집 사용량 = 켜져 있는 (키워드 × 채널) 토글 수.
 * 플랜의 `subscriptionPlan.planService.enhancedSearchLV2` 상한과 같은 단위다.
 * 키워드가 빈 줄은 세지 않는다.
 */
export const countEnhancedCollectionToggles = (list: unknown): number => (Array.isArray(list) ? list : [])
  .reduce((sum: number, entry: any) => {
    if (String(entry?.keyword ?? '').trim() === '') return sum;
    return sum + (Array.isArray(entry?.platforms) ? entry.platforms.length : 0);
  }, 0);

/**
 * 계정 칩(`"인스타그램 : melixir"`) → 저장 모양(`{ platform, account }`).
 *
 * 칩에 플랫폼 라벨이 붙어 있으면 그것을 쓰고, 없으면 `fallbackPlatform` 을 쓴다.
 * 계정이 빈 칩은 버린다.
 *
 * ⚠️ 이 변환이 원래 `saveEdit` 과 `validCampaigns` **두 곳에 같은 글자로** 적혀 있었다.
 *    폴백만 달랐다 — 수정 폼은 `editDraft.platform`, 새 캠페인 폼은 `set.platforms[0]` 이다.
 *    그것을 인자로 뺐다.
 *
 */
export function parseAccountChips(
  chips: unknown,
  /** 라벨이 없을 때 붙일 플랫폼 id (없으면 빈 문자열) */
  fallbackPlatform: string = ''
): { platform: string; account: string }[] {
  return (Array.isArray(chips) ? chips : [])
    .map((chip) => {
      const raw = String(chip || '').trim();
      let platformId = '';
      let account = raw;
      const m = /^([^:]+)\s*:\s*(.+)$/.exec(raw);
      if (m) {
        const label = String(m[1] || '').trim().toLowerCase();
        account = String(m[2] || '').trim();
        const found = (Array.isArray(platforms_SNS) ? platforms_SNS : [])
          .find((p) => String(p?.label || '').toLowerCase() === label);
        platformId = found?.id || '';
      } else {
        platformId = String(fallbackPlatform || '');
      }
      return { platform: platformId, account };
    })
    .filter((a) => a.account !== '');
}

/** 문자열 배열을 다듬어 빈 값을 버린다 */
export function trimList(list: unknown): string[] {
  return (Array.isArray(list) ? list : []).map((v) => String(v || '').trim()).filter((v) => v !== '');
}

/** 새 캠페인 폼 한 칸의 빈 모양 */
export type KeywordSet = {
  campaignName: string;
  keywords: string[];
  platforms: string[];
  accounts: string[];
  commonKeywords: string[];
  commonExcludeKeywords: string[];
  platformFilters: string[];
  countries: string[];
  languages: string[];
  enhancedCollections: EnhancedCollection[];
  messageMetricThreshold: number;
};

export function createEmptyKeywordSet(): KeywordSet {
  return {
    campaignName: '',
    keywords: [],
    platforms: [],
    accounts: [],
    commonKeywords: [],
    commonExcludeKeywords: [],
    platformFilters: [],
    countries: [],
    languages: [],
    enhancedCollections: [],
    messageMetricThreshold: 0,
  };
}
